
import React, { useState, useEffect } from 'react';
import { X, Megaphone, Sparkles, ArrowRight } from 'lucide-react';

interface Props {
  id: string; // Identificador único para salvar no localStorage (ex: 'news-v1.0')
  expiresAt: string; // Formato YYYY-MM-DD
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

export const NewsBanner: React.FC<Props> = ({ id, expiresAt, title, message, actionLabel, onAction }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const checkVisibility = () => {
      // 1. Verifica se já foi dispensado
      const isDismissed = localStorage.getItem(`news_dismissed_${id}`);
      if (isDismissed) return;

      // 2. Verifica se a data expirou
      const now = new Date();
      const expirationDate = new Date(expiresAt);
      
      // Ajusta a data de expiração para o final do dia (23:59:59) para garantir visibilidade no dia limite
      expirationDate.setHours(23, 59, 59, 999);

      if (now <= expirationDate) {
        setIsVisible(true);
      }
    };

    checkVisibility();
  }, [id, expiresAt]);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(`news_dismissed_${id}`, 'true');
  };

  if (!isVisible) return null;

  return (
    <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 border-b border-blue-700/50 shadow-lg relative animate-fade-in z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-start sm:items-center justify-between gap-4">
          
          <div className="flex items-start gap-3 flex-1">
            <div className="bg-blue-500/20 p-2 rounded-lg shrink-0 mt-0.5 sm:mt-0">
              <Megaphone className="w-5 h-5 text-blue-300" />
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm">
              <span className="font-bold text-white flex items-center gap-2">
                {title}
                <Sparkles className="w-3 h-3 text-yellow-400 animate-pulse" />
              </span>
              <span className="hidden sm:inline text-blue-400">|</span>
              <span className="text-blue-100 leading-relaxed">
                {message}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0 pl-2">
            {actionLabel && onAction && (
              <button
                onClick={onAction}
                className="hidden sm:flex items-center gap-1 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 px-3 py-1.5 rounded-full transition-colors"
              >
                {actionLabel} <ArrowRight className="w-3 h-3" />
              </button>
            )}
            
            <button
              onClick={handleDismiss}
              className="text-blue-300 hover:text-white hover:bg-blue-800/50 p-1.5 rounded-lg transition-colors flex items-center gap-1"
              title="Dispensar"
            >
              <span className="text-xs font-semibold sm:hidden">Entendi</span>
              <X className="w-5 h-5" />
            </button>
          </div>

        </div>
        
        {/* Botão de ação mobile (aparece abaixo do texto em telas pequenas) */}
        {actionLabel && onAction && (
           <div className="mt-3 sm:hidden">
              <button
                onClick={onAction}
                className="w-full flex items-center justify-center gap-2 text-xs font-bold text-white bg-blue-600/80 hover:bg-blue-500 px-4 py-2 rounded-lg transition-colors"
              >
                {actionLabel} <ArrowRight className="w-3 h-3" />
              </button>
           </div>
        )}
      </div>
    </div>
  );
};
